import { AxiosError } from 'axios';
import { fetchCurrentAdmin, getErrorsCount, getOrderErrorsCount } from '../../../../actions';
import branditRails from '../../../../api/brandit_rails';
import { AUTH_ACTIONS } from './auth.types';
import { assemblePermissions, assembleSession, decodeAuthToken, getAuthToken, handleErrorMessage, isTokenValid, removeAuthCookie, setAuthToken } from './auth.utils';
export const getCurrentAdmin = () => async (dispatch) => {
    const currentAdmin = await dispatch(fetchCurrentAdmin());
    if (currentAdmin.logged_in_as === 'Organization') {
        await dispatch(getErrorsCount());
        await dispatch(getOrderErrorsCount());
    }
    return currentAdmin;
};
export const initializeAuth = () => async (dispatch) => {
    try {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_TASK, payload: 'fetchStorageToken' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'loading' });
        localStorage.setItem('initialAuth', 'true');
        const token = getAuthToken();
        if (!token || !isTokenValid(token))
            return { isAuthenticated: false, token: null };
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_TASK, payload: 'verifyStorageToken' });
        const tokenPayload = decodeAuthToken(token);
        if (!tokenPayload)
            throw new Error('Invalid token');
        const currentAdmin = await getCurrentAdmin()(dispatch);
        const session = assembleSession(token, tokenPayload, currentAdmin);
        const permissions = assemblePermissions(currentAdmin);
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'succeeded' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_INITIALIZED, payload: true });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_SESSION, payload: session });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_AUTHENTICATED, payload: true });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_PERMISSIONS, payload: permissions });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_LAST_ACTIVITY, payload: Date.now() });
    }
    catch (err) {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'failed' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_ERRORS, payload: err?.message || 'Authentication failed' });
    }
    finally {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_INITIALIZED, payload: true });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'idle' });
    }
};
export const loginWithCredentials = (credentials) => async (dispatch) => {
    try {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_TASK, payload: 'login' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'loading' });
        localStorage.setItem('initialAuth', 'true');
        const { data: token } = await branditRails.request({
            method: 'POST',
            url: 'sessions',
            data: credentials
        });
        if (!token)
            throw new Error('Login attempt failed (missing token)');
        if (!isTokenValid(token))
            throw new Error('Invalid token');
        const redirect = localStorage.getItem('redirectUrl');
        if (redirect && redirect !== '/') {
            localStorage.removeItem('redirectUrl');
            window.location.href = redirect;
        }
        setAuthToken(token); // Store token in cookies and sessionStorage
        const tokenPayload = decodeAuthToken(token);
        if (!tokenPayload)
            throw new Error('Invalid token');
        const currentAdmin = await getCurrentAdmin()(dispatch);
        const session = assembleSession(token, tokenPayload, currentAdmin);
        const permissions = assemblePermissions(currentAdmin);
        // TODO handle current agency/organization
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'succeeded' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_SESSION, payload: session });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_PERMISSIONS, payload: permissions });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_AUTHENTICATED, payload: true });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_LAST_ACTIVITY, payload: Date.now() });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_LOGIN_METHOD, payload: 'credentials' });
    }
    catch (err) {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'failed' });
        dispatch({ type: AUTH_ACTIONS.INCREMENT_AUTH_LOGIN_ATTEMPTS });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_ERRORS, payload: handleErrorMessage(err) });
        dispatch({
            type: AUTH_ACTIONS.SET_AUTH_DEV_TOKEN,
            payload: err instanceof AxiosError ? err.response?.data.dev_token : null
        });
    }
};
export const loginWithAuth0 = credentials => async (dispatch) => {
    try {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_TASK, payload: 'loginWithAuth0' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'loading' });
        const { idToken, user, isAuthenticated } = credentials;
        if (!isAuthenticated || !user || !idToken)
            throw new Error('User is not authenticated');
        const rawToken = idToken.__raw;
        const userData = {
            auth0Token: {
                token: rawToken,
                email: user.email,
                name: user.name,
                uid: user.sub
            }
        };
        localStorage.setItem('initialAuth', 'true');
        const { data: token } = await branditRails.request({
            method: 'POST',
            url: 'sessions',
            data: userData
        });
        if (!token)
            throw new Error('Login attempt failed (missing token)');
        setAuthToken(token); // Store token in cookies and sessionStorage
        if (!token || !isTokenValid(token))
            return { isAuthenticated: false, token: null };
        const tokenPayload = decodeAuthToken(token);
        if (!tokenPayload)
            throw new Error('Invalid token');
        const currentAdmin = await dispatch(getCurrentAdmin());
        const session = assembleSession(token, tokenPayload, currentAdmin);
        const permissions = assemblePermissions(currentAdmin);
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'succeeded' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_SESSION, payload: session });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_AUTHENTICATED, payload: true });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_LOGIN_METHOD, payload: 'auth0' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_LAST_ACTIVITY, payload: Date.now() });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_PERMISSIONS, payload: permissions });
    }
    catch (err) {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'failed' });
        dispatch({ type: AUTH_ACTIONS.INCREMENT_AUTH_LOGIN_ATTEMPTS });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_ERRORS, payload: handleErrorMessage(err) });
    }
};
export const loginWithSSO = (ssoId, ssoToken) => async (dispatch, getState) => {
    try {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_TASK, payload: 'login' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'loading' });
        if (!ssoId || !ssoToken)
            throw new Error('SSO ID and token are required.');
        const getStoreDomain = () => {
            return new Promise(resolve => {
                const queryParams = new URLSearchParams(window.location.search);
                const domain = queryParams.get('store_domain') ?? '/login';
                resolve(domain);
            });
        };
        const storeDomain = await getStoreDomain();
        const logoutDestination = storeDomain;
        localStorage.setItem('store_domain', logoutDestination);
        const tokenPayload = decodeAuthToken(ssoToken);
        const currentAdmin = getState()?.admins?.current_admin;
        const session = assembleSession(ssoToken, tokenPayload, currentAdmin);
        const permissions = assemblePermissions(currentAdmin);
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'succeeded' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_SESSION, payload: session });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_AUTHENTICATED, payload: true });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_PERMISSIONS, payload: permissions });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_LOGIN_METHOD, payload: 'sso' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_LAST_ACTIVITY, payload: Date.now() });
    }
    catch (err) {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'failed' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_ERRORS, payload: handleErrorMessage(err) });
    }
};
export const forgotPasswordRequest = email => async (dispatch) => {
    try {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_TASK, payload: 'forgotPassword' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'loading' });
        await branditRails.request({
            method: 'POST',
            url: 'admins/forgot_password',
            data: { admin: { email } }
        });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'succeeded' });
    }
    catch (err) {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'failed' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_ERRORS, payload: handleErrorMessage(err) });
    }
};
export const verifyPasswordToken = token => async (dispatch) => {
    try {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_TASK, payload: 'verifyPasswordToken' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'loading' });
        if (!token)
            throw new Error('Missing token');
        await branditRails.request({ url: 'admins/verify_password_token', method: 'POST', data: { admin: { token } } });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'succeeded' });
    }
    catch (err) {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'failed' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_ERRORS, payload: handleErrorMessage(err) });
    }
};
export const resetPassword = data => async (dispatch) => {
    try {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_TASK, payload: 'resetPassword' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'loading' });
        if (!data.token)
            throw new Error('Missing token');
        await branditRails.request({
            url: `admins/reset_password/${data.token}`,
            method: 'PUT',
            data: { admin: { password: data.newPassword, password_confirmation: data.confirmPassword } }
        });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'succeeded' });
    }
    catch (err) {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'failed' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_ERRORS, payload: handleErrorMessage(err) });
    }
};
export const universalLogout = () => async (dispatch, getState) => {
    try {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_TASK, payload: 'logout' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'loading' });
        const currentStoreDomain = localStorage.getItem('store_domain');
        const currentAdmin = getState()?.admins?.current_admin;
        const associatedPortalInfo = currentAdmin.associated_portal_info;
        const shouldRedirectToStore = currentStoreDomain && currentStoreDomain.length > 0;
        const storeUrl = currentStoreDomain ??
            (associatedPortalInfo && associatedPortalInfo[0] ? associatedPortalInfo[0].store_domain : '/');
        await branditRails.request({
            method: 'DELETE',
            url: currentAdmin.logged_in_as === 'Agency' ? 'sessions' : 'admin/organization/sessions'
        });
        removeAuthCookie();
        sessionStorage.clear();
        localStorage.removeItem('store_domain');
        if (typeof window.Featurebase === 'function')
            window.Featurebase('shutdown');
        if (shouldRedirectToStore)
            window.location.href = `${storeUrl}?logout=true`;
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
    catch (err) {
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_STATUS, payload: 'failed' });
        dispatch({ type: AUTH_ACTIONS.SET_AUTH_ERRORS, payload: handleErrorMessage(err) });
    }
};
